import { Participation, Players, Tableau } from "../../../types";

export type OverlapScope = "all" | "paid" | "checked_in";

export type BoardPairOverlap = {
  boardAId: string;
  boardBId: string;
  boardAName: string;
  boardBName: string;
  boardALabel: string;
  boardBLabel: string;
  playerIds: string[];
  players: Players[];
  count: number;
};

/** Clé de journée locale `YYYY-MM-DD`. */
export function boardDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatBoardDayLabel(dayKey: string): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!y || !m || !d) return dayKey;
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function groupBoardsByDay(boards: Tableau[]): Map<string, Tableau[]> {
  const map = new Map<string, Tableau[]>();
  for (const board of boards) {
    if (!board?.id || !(board.date instanceof Date) || Number.isNaN(board.date.getTime())) {
      continue;
    }
    const key = boardDayKey(board.date);
    const list = map.get(key) ?? [];
    list.push(board);
    map.set(key, list);
  }
  map.forEach((list) => {
    list.sort((a: Tableau, b: Tableau) =>
      String(a.name || a.label || "").localeCompare(
        String(b.name || b.label || ""),
        "fr",
        { sensitivity: "base" }
      )
    );
  });
  return map;
}

export function participationMatchesScope(
  participation: Participation,
  scope: OverlapScope
): boolean {
  const status = participation.paymentStatus;
  if (status === "cancelled" || status === "refunded") {
    return false;
  }
  if (scope === "all") {
    return true;
  }
  if (scope === "paid") {
    return status === "registration" || status === "admin" || status === "offert";
  }
  return participation.checkedIn === true;
}

export function filterParticipations(
  participations: Participation[],
  scope: OverlapScope
): Participation[] {
  return participations.filter((p) => participationMatchesScope(p, scope));
}

function boardDisplayName(board: Tableau): string {
  return String(board.name || board.label || board.id || "?").trim();
}

function boardDisplayLabel(board: Tableau): string {
  return String(board.label || board.name || board.id || "?").trim();
}

/**
 * Calcule toutes les intersections joueurs entre paires de tableaux du jour.
 * Utilise `board.participation` (déjà hydratées) filtrées par `scope`.
 */
export function computeBoardPairsOverlaps(
  boardsOfDay: Tableau[],
  scope: OverlapScope
): BoardPairOverlap[] {
  const boards = [...boardsOfDay]
    .filter((b) => b.id != null)
    .sort((a, b) =>
      boardDisplayName(a).localeCompare(boardDisplayName(b), "fr", {
        sensitivity: "base",
      })
    );

  const playersByBoard = new Map<string, Map<string, Players>>();
  for (const board of boards) {
    const byPlayer = new Map<string, Players>();
    const parts = filterParticipations(board.participation ?? [], scope);
    for (const part of parts) {
      const playerId = part.playersId != null ? String(part.playersId) : "";
      if (!playerId || !part.players) continue;
      if (!byPlayer.has(playerId)) {
        byPlayer.set(playerId, part.players);
      }
    }
    playersByBoard.set(String(board.id), byPlayer);
  }

  const overlaps: BoardPairOverlap[] = [];
  for (let i = 0; i < boards.length; i += 1) {
    for (let j = i + 1; j < boards.length; j += 1) {
      const boardA = boards[i];
      const boardB = boards[j];
      const mapA = playersByBoard.get(String(boardA.id)) ?? new Map();
      const mapB = playersByBoard.get(String(boardB.id)) ?? new Map();
      const playerIds: string[] = [];
      const players: Players[] = [];
      mapA.forEach((player, playerId) => {
        if (mapB.has(playerId)) {
          playerIds.push(playerId);
          players.push(player);
        }
      });
      players.sort((a, b) =>
        `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, "fr", {
          sensitivity: "base",
        })
      );
      overlaps.push({
        boardAId: String(boardA.id),
        boardBId: String(boardB.id),
        boardAName: boardDisplayName(boardA),
        boardBName: boardDisplayName(boardB),
        boardALabel: boardDisplayLabel(boardA),
        boardBLabel: boardDisplayLabel(boardB),
        playerIds,
        players,
        count: players.length,
      });
    }
  }

  return overlaps.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    const labelA = `${a.boardAName}/${a.boardBName}`;
    const labelB = `${b.boardAName}/${b.boardBName}`;
    return labelA.localeCompare(labelB, "fr", { sensitivity: "base" });
  });
}

export function findOverlapForPair(
  overlaps: BoardPairOverlap[],
  boardAId: string,
  boardBId: string
): BoardPairOverlap | undefined {
  const a = String(boardAId);
  const b = String(boardBId);
  return overlaps.find(
    (o) =>
      (o.boardAId === a && o.boardBId === b) ||
      (o.boardAId === b && o.boardBId === a)
  );
}
